/**
 * The cross-tab lock auth-js takes around every session read and refresh.
 *
 * auth-js waits for it with no time limit. A tab that froze or hung while
 * holding it (Safari suspends background tabs, a token refresh stalls after
 * sleep) left every other tab of the site stuck: getSession() never returned,
 * and a correct password sat on "Loading" on /auth. So a wait is bounded, and
 * then the lock is taken over. Timeout 0 (the auto-refresh tick's "only if
 * free") keeps its meaning.
 *
 * Taking a lock over rejects its holder's request with an AbortError while
 * the holder's work carries on. That rejection reached auth-js mid-call and
 * surfaced as an unhandled "Lock broken by another request with the 'steal'
 * option". A holder whose work has started therefore waits for that work and
 * returns its result, as if it still held the lock.
 */
export type LockFn = <R>(name: string, acquireTimeout: number, fn: () => Promise<R>) => Promise<R>;
export type StealFn = <R>(name: string, fn: () => Promise<R>) => Promise<R>;

export const SESSION_LOCK_WAIT_MS = 5000;

const isAbort = (e: unknown) => (e as { name?: unknown } | null)?.name === 'AbortError';

export function createSessionLock(lock: LockFn, steal: StealFn, waitMs = SESSION_LOCK_WAIT_MS): LockFn {
  return async <R>(name: string, acquireTimeout: number, fn: () => Promise<R>): Promise<R> => {
    let running: Promise<R> | null = null;
    const run = () => (running = fn());
    const finishIfTakenOver = (e: unknown): Promise<R> => {
      if (running && isAbort(e)) return running;
      throw e;
    };
    if (acquireTimeout >= 0) return lock(name, acquireTimeout, run).catch(finishIfTakenOver);
    try {
      return await lock(name, waitMs, run);
    } catch (e) {
      if (running || !isAbort(e)) return finishIfTakenOver(e);
      console.warn('[Supabase Client] Session lock held by another tab, taking it over');
      return steal(name, run).catch(finishIfTakenOver);
    }
  };
}
